import { useQuery } from '@tanstack/react-query';
import { Loader2, PhoneCall, ShieldAlert, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DataTable, type Column } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { Filters, useFilters, type FilterField } from '../components/Filters';
import { SelectionBar, type BulkAction } from '../components/SelectionBar';
import { HelpHint } from '../components/HelpHint';
import { HoverCard } from '../components/Hover';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';
import type * as T from '../lib/types';

const BUCKETS: { key: keyof T.Ageing; label: string; tone: string }[] = [
  { key: 'current', label: 'Not yet due', tone: 'text-muted-foreground' },
  { key: 'd1_30', label: '1–30 days', tone: 'text-navy-700' },
  { key: 'd31_60', label: '31–60 days', tone: 'text-amber-700' },
  { key: 'd61_90', label: '61–90 days', tone: 'text-orange-700' },
  { key: 'd90_plus', label: 'Over 90 days', tone: 'text-danger-700' },
];

const STATE_LABEL: Record<T.DunningState, string> = {
  ok: 'Up to date',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  in_grace: 'In grace',
  due_for_suspension: 'Due for suspension',
};

const STATE_TONE: Record<T.DunningState, string> = {
  ok: 'bg-emerald-50 text-emerald-700',
  due_soon: 'bg-navy-50 text-navy-700',
  overdue: 'bg-amber-50 text-amber-700',
  in_grace: 'bg-amber-50 text-amber-700',
  due_for_suspension: 'bg-danger-50 text-danger-700',
};

const kes = (cents: number) =>
  new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    maximumFractionDigits: 0,
  }).format(cents / 100);

/**
 * What is owed, and how old it is.
 *
 * ## The ageing is the point, not the total
 *
 * Fifty thousand spread across this month's invoices is a business trading
 * normally. The same fifty thousand sitting in the over-90 column is money
 * nobody is going to collect. They are the same number and they mean opposite
 * things, so the breakdown comes first on the page and the total is a
 * consequence of it.
 *
 * ## Nothing here disconnects anybody
 *
 * The page will tell you an account is due for suspension. It will not suspend
 * it. That is a decision somebody takes on the subscription itself, with a
 * reason, and it is audited — because "the system cut them off" is not an
 * answer anyone should have to give a customer.
 */
export function ArrearsPage() {
  const arrears = useQuery({ queryKey: ['arrears'], queryFn: () => api.arrears() });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const all = useMemo(() => arrears.data?.customers ?? [], [arrears.data]);

  const FIELDS: FilterField<T.ArrearsCustomer>[] = useMemo(
    () => [
      { key: 'name', label: 'Customer', type: 'text', value: (row) => row.name },
      { key: 'account', label: 'Account', type: 'text', value: (row) => row.accountRef ?? row.phone },
      { key: 'owed', label: 'Owed', type: 'number', value: (row) => row.owedCents / 100 },
      { key: 'invoices', label: 'Invoices', type: 'number', value: (row) => row.invoiceCount },
      {
        key: 'state',
        label: 'Standing',
        type: 'choice',
        options: Object.entries(STATE_LABEL).map(([value, label]) => ({ value, label })),
        value: (row) => row.state,
      },
    ],
    [],
  );

  const { query, setQuery, clauses, setClauses, filtered: customers } = useFilters(all, FIELDS);

  /** The oldest bucket with money in it, which is what "how bad" means here. */
  const oldestFor = (row: T.ArrearsCustomer) =>
    BUCKETS.slice()
      .reverse()
      .find((bucket) => (row.ageing[bucket.key] as number) > 0) ?? null;

  /*
   * A collection list is worked from paper and from a spreadsheet as often as
   * from the screen — somebody takes it into a meeting, or rings down it. So
   * the two things this page most needed were an export and a print, and
   * neither existed.
   *
   * There are no edit or delete actions here on purpose. This is a view over
   * invoices; the way to change what it says is to take a payment or raise a
   * credit note, both of which happen where those records live and both of
   * which leave a trail. A button here that altered a balance would be the one
   * place in this system money moved without a document behind it.
   */
  const actions: BulkAction<T.ArrearsCustomer>[] = [
    { key: 'print', label: 'Print', prominent: true, run: () => window.print() },
  ];

  const columns: Column<T.ArrearsCustomer>[] = [
    {
      key: 'name',
      header: 'Customer',
      sortBy: (row) => row.name.toLowerCase(),
      cell: (row) => (
        /*
          The ageing for this one customer, on hover. It is the answer to "how
          bad is this account really", which the single owed figure cannot
          give — and putting five more columns on the table to show it would
          make the table unreadable on a laptop.
        */
        <HoverCard
          title={row.name}
          facts={BUCKETS.filter((bucket) => (row.ageing[bucket.key] as number) > 0).map(
            (bucket) => ({
              label: bucket.label,
              value: kes(row.ageing[bucket.key] as number),
            }),
          )}
          note={row.message ?? undefined}
          side="right"
        >
          <span className="font-medium">{row.name}</span>
        </HoverCard>
      ),
    },
    {
      key: 'account',
      header: 'Account',
      secondary: true,
      sortBy: (row) => row.accountRef ?? row.phone,
      cell: (row) => (
        <a
          href={`tel:${row.phone}`}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-navy-700 hover:underline"
        >
          <PhoneCall className="h-3 w-3" aria-hidden="true" />
          {row.accountRef ?? row.phone}
        </a>
      ),
    },
    {
      key: 'owed',
      header: 'Owed',
      numeric: true,
      /*
       * On the cents, not on the formatted string. "KES 9,000" sorts above
       * "KES 10,000" as text, which would put the largest debts in the middle
       * of the list — on the one page where the largest debts are the point.
       */
      sortBy: (row) => row.owedCents,
      cell: (row) => <span className="font-medium tabular-nums">{row.owed}</span>,
    },
    {
      key: 'invoices',
      header: 'Invoices',
      numeric: true,
      sortBy: (row) => row.invoiceCount,
      cell: (row) => row.invoiceCount,
    },
    {
      key: 'oldest',
      header: 'Oldest debt',
      secondary: true,
      /*
       * Ordered by how old, not alphabetically. "1–30 days" before "Over 90"
       * is the only order that means anything, and the label sorts the other
       * way round.
       */
      sortBy: (row) => BUCKETS.findIndex((bucket) => bucket.key === oldestFor(row)?.key),
      cell: (row) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {oldestFor(row)?.label ?? '—'}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'Standing',
      sortBy: (row) => row.state,
      cell: (row) => <span className={`badge ${STATE_TONE[row.state]}`}>{STATE_LABEL[row.state]}</span>,
    },
  ];

  if (arrears.isPending) {
    return (
      <div className="space-y-6">
        <PageNav crumbs={[{ label: 'Arrears' }]} />
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Working out what is owed
        </p>
      </div>
    );
  }

  const data = arrears.data;

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Arrears' }]} />

      <div>
        <h1 className="flex items-center gap-1.5 font-heading text-2xl">
          Arrears
          <HelpHint>
            How old the money owed is, not just how much. An account moves into grace the day
            after its invoice falls due, and is flagged for suspension once the grace period runs
            out — but nothing is ever suspended automatically.
          </HelpHint>
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {customers.length === 0
            ? 'Everybody is up to date.'
            : `${customers.length} customer${customers.length === 1 ? '' : 's'} owing ${kes(data!.totals.totalCents)}.`}
        </p>
      </div>

      {/* ── The ageing ────────────────────────────────────────────────────── */}
      {data && data.totals.totalCents > 0 && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {BUCKETS.map((bucket) => (
            <div key={bucket.key} className="card">
              <p className="text-xs label-mono tracking-[0.12em] text-muted-foreground">
                {bucket.label}
              </p>
              <p className={`mt-1 font-heading text-xl tabular-nums ${bucket.tone}`}>
                {kes(data.totals[bucket.key] as number)}
              </p>
            </div>
          ))}
        </div>
      )}

      {data && data.dueForSuspension > 0 && (
        <p className="flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {data.dueForSuspension} account{data.dueForSuspension === 1 ? ' is' : 's are'} past the{' '}
          {data.policy.graceDays}-day grace period. Suspending one is done on the subscription
          itself, so that it carries a reason and a name.
        </p>
      )}

      {customers.length === 0 && (
        <EmptyState
          icon={Wallet}
          headline="Nothing outstanding"
          body="Every invoice raised has been paid. This page fills up on its own once the billing runs."
        />
      )}

      {customers.length > 0 && (
        <>
          <Filters<T.ArrearsCustomer>
            fields={FIELDS}
            query={query}
            onQuery={setQuery}
            clauses={clauses}
            onClauses={setClauses}
            total={all.length}
            showing={customers.length}
            placeholder="Search by customer, account or phone"
          />

          <SelectionBar<T.ArrearsCustomer>
            rows={customers}
            selected={selected}
            onClear={() => setSelected(new Set())}
            rowKey={(row) => row.customerId}
            exportName="arrears"
            csv={[
              { header: 'Customer', value: (row) => row.name },
              { header: 'Account', value: (row) => row.accountRef ?? '' },
              { header: 'Phone', value: (row) => row.phone },
              { header: 'Owed', value: (row) => row.owedCents / 100 },
              { header: 'Invoices', value: (row) => row.invoiceCount },
              { header: 'Oldest debt', value: (row) => oldestFor(row)?.label ?? '' },
              { header: 'Standing', value: (row) => STATE_LABEL[row.state] },
              ...BUCKETS.map((bucket) => ({
                header: bucket.label,
                value: (row: T.ArrearsCustomer) => (row.ageing[bucket.key] as number) / 100,
              })),
            ]}
            actions={actions}
          />

          <DataTable<T.ArrearsCustomer>
            rows={customers}
            columns={columns}
            rowKey={(row) => row.customerId}
            selected={selected}
            onSelect={setSelected}
            defaultSort={{ key: 'owed', direction: 'desc' }}
            caption={`${customers.length} ${customers.length === 1 ? 'account' : 'accounts'} owing`}
            empty={<p className="py-6 text-center text-sm text-muted-foreground">Nobody owes anything.</p>}
          />
        </>
      )}
    </div>
  );
}
